import React, { useContext, useState, useRef } from "react";
import { AppContext } from "../App";
import { FiMail, FiMapPin, FiPhone, FiMessageSquare, FiArrowRight, FiClock } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import { callApi } from '../Helpers';

// Asset Allocation from project directory
import banner1 from "../assets/banner1.jpg";

export default function ContactUsContent() {
  const appContext = useContext(AppContext);
  
  // Form State Configuration
  const [contactInfo, setContactInfo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  
  // CAPTCHA State Management
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // API Call Handler
  const submitInquiry = async () => {
    if (contactInfo && contactInfo.trim().length > 0) {
      if (subject && subject.trim().length > 0) {
        if (message && message.trim().length > 0) {
          if (captchaToken) {
            setLoading(true);
            
            await callApi(
              'public/send-inquiry',
              {
                emailOrPhone: contactInfo,
                subject: subject,
                message: message
              }
            ).then(response => {
              if (response.status === 1) {
                appContext.tellMessage('Inquiry Submitted Successfully');
                
                // Clear UI fields seamlessly
                setContactInfo("");
                setSubject("");
                setMessage("");
                setCaptchaToken(null);
                if (recaptchaRef.current) {
                  recaptchaRef.current.reset();
                }
              } else {
                appContext.tellError(response.msg || "Failed to process request");
              }
            }).catch(error => {
                appContext.tellError("Network or server error encountered");
            });

            setLoading(false);
          } else {
            appContext.tellError("Please complete the CAPTCHA verification");
          }
        } else {
          appContext.tellError("Invalid message content");
        }
      } else {
        appContext.tellError("Invalid subject entry");
      }
    } else {
      appContext.tellError("Invalid email or phone number");
    }
  };

  return (
    <>
      <style>{`
        :root {
          --vibe-dark: #0A0F18;
          --vibe-slate: #111A2E;
          --vibe-border: #1E2D4A;
          --vibe-orange: #ED8936;
          --vibe-green: #38A169;
          --whatsapp-green: #25d366;
          --text-slate: #CBD5E0;
          --text-muted: #718096;
          --white: #ffffff;
          --font-main: 'Libre Franklin', -apple-system, sans-serif;
          --transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .cu-section {
          background: var(--vibe-dark);
          padding: clamp(60px, 10vh, 120px) 0;
          font-family: var(--font-main);
          color: var(--white);
        }

        .cu-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ── Typography Headers ── */
        .cu-header {
          max-width: 700px;
          margin-bottom: clamp(40px, 8vh, 80px);
        }

        .cu-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.2rem, 5vw, 3.8rem);
          font-weight: 500;
          line-height: 1.2;
          margin: 0;
        }

        /* ── Grid Framework ── */
        .cu-grid {
          display: grid;
          grid-template-columns: 1fr 1.1fr;
          gap: 48px;
          align-items: stretch;
        }

        /* ── Visual Media Element ── */
        .cu-visual {
          position: relative;
          border-radius: 6px;
          border: 1px solid var(--vibe-border);
          overflow: hidden;
          min-height: 440px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }

        .cu-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          inset: 0;
        }

        .cu-img-overlay {
          position: absolute;
          background: linear-gradient(to bottom, var(--vibe-slate), transparent);
          opacity: 0.6;
        }

        .cu-glass-card {
          position: absolute;
          bottom: 0;
          left: 24px;
          right: 24px;
          background: rgba(17, 26, 46, 0.8);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          padding: 24px;
          border-radius: 4px;
          color: var(--white);
        }

        /* ── Operational Shortcut Elements ── */
        .cu-actions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .cu-action-card {
          background: var(--vibe-slate);
          border: 1px solid var(--vibe-border);
          padding: 20px 24px;
          border-radius: 4px;
          display: grid;
          grid-template-columns: 48px 1fr 24px;
          align-items: center;
          text-decoration: none;
          color: inherit;
          transition: var(--transition);
        }

        .cu-action-card:hover {
          transform: translateY(-3px);
          border-color: var(--vibe-orange);
          background: rgba(17, 26, 46, 0.9);
          box-shadow: 0 15px 30px rgba(0,0,0,0.2);
        }

        .cu-icon-box {
          width: 36px;
          height: 36px;
          background: var(--vibe-dark);
          border: 1px solid var(--vibe-border);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          color: var(--vibe-orange);
        }

        .cu-card-meta h4 {
          margin: 0 0 2px 0;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--text-muted);
        }

        .cu-card-meta p {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 500;
          letter-spacing: -0.01em;
          color: var(--white);
          word-break: break-all;
        }

        .cu-arrow {
          font-size: 1.1rem;
          color: var(--text-muted);
          text-align: right;
          transition: var(--transition);
        }

        .cu-action-card:hover .cu-arrow {
          color: var(--vibe-orange);
          transform: translateX(4px);
        }

        /* ── Input Interactive Block ── */
        .cu-form-container {
          background: var(--vibe-slate);
          border: 1px solid var(--vibe-border);
          padding: 32px;
          border-radius: 4px;
          margin-top: 8px;
        }

        .cu-input {
          width: 100%;
          padding: 14px 16px;
          margin-bottom: 14px;
          background: var(--vibe-dark);
          border: 1px solid var(--vibe-border);
          color: var(--white);
          border-radius: 4px;
          font-family: inherit;
          font-size: 0.95rem;
          box-sizing: border-box;
          transition: var(--transition);
        }

        .cu-input:focus {
          outline: none;
          border-color: var(--vibe-orange);
          box-shadow: 0 0 0 3px rgba(237, 137, 54, 0.15);
        }

        .cu-input::placeholder {
          color: #4A5568;
        }

        .cu-textarea {
          resize: vertical;
          min-height: 100px;
          margin-bottom: 6px;
        }

        .cu-char-count {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-align: right;
          margin-bottom: 20px;
        }

        .cu-captcha-wrapper {
          width: 100%;
          margin-bottom: 20px;
          display: flex;
          justify-content: center;
          overflow: hidden;
        }

        @media (max-width: 360px) {
          .cu-captcha-wrapper > div {
            transform: scale(0.85);
            transform-origin: center center;
          }
        }

        .cu-btn {
          background: var(--vibe-orange);
          color: var(--vibe-dark);
          border: none;
          padding: 14px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.95rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          box-sizing: border-box;
          transition: var(--transition);
        }

        .cu-btn:hover {
          background: var(--white);
          color: var(--vibe-dark);
        }

        .cu-btn-placeholder {
          background: rgba(30, 45, 74, 0.4);
          color: var(--text-muted);
          border: 1px solid var(--vibe-border);
          padding: 14px 24px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 0.95rem;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: not-allowed;
          box-sizing: border-box;
        }

        /* ── Structural Breakpoints ── */
        @media (max-width: 992px) {
          .cu-grid { 
            grid-template-columns: 1fr; 
            gap: 32px;
          }
          
          .cu-header {
            text-align: center;
            margin-left: auto;
            margin-right: auto;
          }

          .cu-visual {
            order: -1; 
            min-height: 320px;
          }

          .cu-action-card {
            grid-template-columns: 48px 1fr; 
          }
          
          .cu-arrow { display: none; }

          .cu-info-footer {
            text-align: center;
            border-left: none !important;
            border-top: 1px solid var(--vibe-border);
            padding-top: 24px !important;
          }
        }

        @media (max-width: 480px) {
          .cu-container { padding: 0 16px; }

          .cu-glass-card {
            position: relative;
            bottom: 0;
            left: 0;
            right: 0;
            background: var(--vibe-slate);
            border-radius: 0 0 6px 6px;
            margin-top: -8px;
            padding: 20px;
          }
          
          .cu-action-card, .cu-form-container { padding: 20px 16px; }
        }
      `}</style>

      <section className="cu-section">
        <div className="cu-container">
          
          <header className="cu-header">
            <h2 className="cu-title">Reliable Corporate <br/><span style={{ color: 'var(--vibe-orange)' }}>Inquiry Channels.</span></h2>
          </header>

          <div className="cu-grid">
            
            {/* Visual Header Map & Address Representation Frame */}
            <div className="cu-visual">
              <img src={banner1} className="cu-img" alt="Tanzavibe Arusha Corporate Registry Hub" />
              <div className="cu-img-overlay" />
              
              <div className="cu-glass-card">
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <FiMapPin size={20} color="var(--vibe-orange)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--text-muted)' }}>Registered Head Office</h4>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: '0.95rem', lineHeight: 1.5, marginTop: '4px', color: '#FFF' }}>
                      Plot No. 1, Olkerien<br/>
                      Arusha Municipal Council<br/>
                      P.O. Box 15341, Arusha, Tanzania
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Functional Operational Shortcut Triggers */}
            <div className="cu-actions-list">
              
              {/* WhatsApp Live Connectivity Block */}
              <a href="https://wa.me/255" target="_blank" rel="noreferrer" className="cu-action-card">
                <div className="cu-icon-box" style={{ color: 'var(--whatsapp-green)' }}>
                  <FaWhatsapp />
                </div>
                <div className="cu-card-meta">
                  <h4>Agribusiness Desk</h4>
                  <p>WhatsApp Live Connection</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* General Inquiries Corporate Line */}
              <a href="tel:+255" className="cu-action-card">
                <div className="cu-icon-box">
                  <FiPhone />
                </div>
                <div className="cu-card-meta">
                  <h4>General Inquiries</h4>
                  <p>+255 111 111 111</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Secondary Infrastructure Support Line */}
              <a href="tel:+" className="cu-action-card">
                <div className="cu-icon-box" style={{ color: 'var(--vibe-green)' }}>
                  <FiPhone />
                </div>
                <div className="cu-card-meta">
                  <h4>Drilling Solutions Support</h4>
                  <p>+255 111 111 111</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Functional Registered Email Transport Protocol */}
              <a href="mailto:info@tanzavibe.co.tz" className="cu-action-card">
                <div className="cu-icon-box">
                  <FiMail />
                </div>
                <div className="cu-card-meta">
                  <h4>Corporate Correspondence</h4>
                  <p>info@tanzavibe.co.tz</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Operating Info Metadata Rule Box */}
              <div className="cu-info-footer" style={{ marginTop: '16px', padding: '0 20px', borderLeft: '3px solid var(--vibe-orange)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--vibe-orange)', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <FiClock /> Business Operating Scale
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-slate)', lineHeight: 1.6 }}>
                  <b>Office Hours:</b> Monday — Friday, <strong>08:00 AM - 05:00 PM EAT</strong><br/>
                </p>
              </div>

            </div>
          </div>

        </div>
      </section>
    </>
  );
}